import React, { useEffect, useState, useMemo } from 'react';
import {
  ClipboardList, ShieldCheck, Briefcase, GraduationCap,
  UserCircle, CalendarClock,
} from 'lucide-react';
import { aepAPI } from '@/lib/api';
import { formatDate } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';

/**
 * AgentSnapshot — dashboard-scoped operational snapshot for the logged-in
 * agent. Six executive-signal cells. Deliberately excludes production,
 * sales, revenue, commission, and performance-ranking metrics per Phase
 * 20.6 guardrails.
 *
 *   1. Contract Requests       → counts.total_requests (all-time).
 *   2. Active Appointments     → appointments filtered by
 *      appointment_status === 'Active' AND writing_number populated
 *      (canonical taxonomy rule).
 *   3. Enabled Carriers        → distinct carrier_id across the agent's
 *      active appointments. Agents don't have agency-style enablement rows;
 *      the closest per-agent signal is "carriers I currently write for".
 *   4. Certifications Completed → count of AEP certifications with a
 *      completed status. Self-fetched from `aepAPI.myCertifications()` —
 *      returns 0 (and the '—' fallback) if the endpoint fails.
 *   5. Portal Access           → agent.status ('Active' / 'Onboarding' /
 *      'Inactive'). Human-readable label of what the agent can do today.
 *   6. Last Updated            → most recent modification on the agent
 *      record. Falls back to created_at.
 */

const Cell = ({ label, value, hint, icon: Icon, testId }) => (
  <div className="flex items-start gap-3" data-testid={testId}>
    {Icon && (
      <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 bg-slate-100 text-slate-600">
        <Icon size={17} />
      </div>
    )}
    <div className="min-w-0">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
        {label}
      </div>
      <div className="text-sm font-semibold mt-0.5 text-slate-900 truncate">
        {value ?? '—'}
      </div>
      {hint && <div className="text-[11px] text-slate-500 mt-0.5">{hint}</div>}
    </div>
  </div>
);

const COMPLETED_CERT_STATUSES = new Set(['Completed', 'completed', 'complete', 'Done', 'done']);

export const AgentSnapshot = ({ profile, parentLoading = false }) => {
  const [certsCompleted, setCertsCompleted] = useState(null);
  const [certsLoading, setCertsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await aepAPI.myCertifications();
        if (cancelled) return;
        const rows = Array.isArray(res?.data) ? res.data : Array.isArray(res?.data?.items) ? res.data.items : [];
        const done = rows.filter((c) => COMPLETED_CERT_STATUSES.has(c?.status)).length;
        setCertsCompleted(done);
      } catch (e) {
        if (cancelled) return;
        console.error('Failed to load agent certifications:', e);
        setCertsCompleted(null);
      } finally {
        if (!cancelled) setCertsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const agent = profile?.agent;
  const counts = profile?.counts || {};
  const appointments = profile?.appointments || [];

  // KR treats an Approved carrier appointment as Active (Approved = Active).
  // `carrier_appointments` is the single source of truth; the contracting
  // approval flow writes status "Approved" (and requires a writing_number),
  // so we accept both to stay consistent with Agent Profile / Admin counts.
  const isActiveAppt = (a) =>
    (a?.appointment_status === 'Active' || a?.appointment_status === 'Approved') && a?.writing_number;

  const activeAppointments = useMemo(
    () => appointments.filter(isActiveAppt).length,
    [appointments],
  );

  const enabledCarriers = useMemo(() => {
    const ids = new Set();
    for (const a of appointments) {
      if (isActiveAppt(a) && a?.carrier_id) {
        ids.add(a.carrier_id);
      }
    }
    return ids.size;
  }, [appointments]);

  const contractRequests = counts?.total_requests ?? (profile?.requests?.length || 0);
  const portalAccess = agent?.status ? String(agent.status).replace(/^./, (c) => c.toUpperCase()) : '—';
  const lastUpdated = agent?.updated_at || agent?.created_at;
  const loading = parentLoading || certsLoading;

  return (
    <section className="mb-8" data-testid="agent-snapshot">
      <div className="mb-3">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
          Agent Snapshot
        </h2>
        <p className="text-sm text-slate-500 mt-1">
          Your contracting footprint at a glance — requests, active writing
          numbers, and account state.
        </p>
      </div>

      <Card className="animate-slideIn">
        <CardContent className="p-5">
          {loading ? (
            <div className="py-6 text-center text-sm text-slate-400" data-testid="agent-snapshot-loading">
              Loading snapshot…
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-5">
              <Cell label="Contracts" value={contractRequests} icon={ClipboardList}
                testId="agent-snapshot-contract-requests"
                hint="All contract requests submitted for you" />
              <Cell label="Active Appointments" value={activeAppointments} icon={ShieldCheck}
                testId="agent-snapshot-active-appointments"
                hint="Writing numbers you currently hold" />
              <Cell label="Enabled Carriers" value={enabledCarriers} icon={Briefcase}
                testId="agent-snapshot-enabled-carriers"
                hint="Distinct carriers you write for" />
              <Cell label="Certifications Completed" value={certsCompleted ?? '—'} icon={GraduationCap}
                testId="agent-snapshot-certifications-completed"
                hint="Per AEP My Certifications" />
              <Cell label="Portal Access" value={portalAccess} icon={UserCircle}
                testId="agent-snapshot-portal-access"
                hint="Your current account status" />
              <Cell label="Last Updated" value={lastUpdated ? formatDate(lastUpdated) : '—'} icon={CalendarClock}
                testId="agent-snapshot-last-updated" />
            </div>
          )}
        </CardContent>
      </Card>
    </section>
  );
};

export default AgentSnapshot;
